/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { FullProduct } from '@msdyn365-commerce/commerce-entities';
import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { addOrderTemplateToCartAction, AddOrderTemplateToCartInput } from '../../order-template-list/actions/add-order-template-to-cart';
import { Button, getPayloadObject, getTelemetryAttributes, IPopupProductsProps, ITelemetryContent, PopupProducts, TelemetryConstant } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import React, { useState } from 'react';
import { OrderTemplate,IFullProduct } from '../../../themes/adventureworks/views/common/order-templates';
import { ILinesAddedToCartDialogResources, LinesAddedToCartDialog } from '@msdyn365-commerce/components';
import { ConfirmAddedToCartDialog } from './add-to-cart-confirmation-dialog-component';
import { IProductInventoryInformation } from '@msdyn365-commerce-modules/retail-actions';
import { Customer, format } from '@msdyn365-commerce/retail-proxy';

export interface IAddOrderTemplateToCartComponentData {
    orderTemplate: OrderTemplate;
}

export interface IAddOrderTemplateToCartComponentProps extends IComponentProps<IAddOrderTemplateToCartComponentData> {
    className?: string;
    parentElementRef?: React.RefObject<Button>;
    addToCartText: string;
    telemetryContent?: ITelemetryContent;
    dialogStrings: {
        closeWindowButtonText: string;
        addToCartFailureMessage: string;
        addToCartSuccessMessage: string;
        addToCartProcessMessage: string;
        linesAddedToCartDialogResources: ILinesAddedToCartDialogResources;
        confirmHeaderText: string;
        confirmBodyText: string;
        alternateItemHeaderText: string;
        alternateItemBodyText: string;
    };
    customerInformation: Customer | undefined;
}

export declare type IAddToCartActionFailureReason = 'EMPTYINPUT' | 'MISSINGDIMENSION' | 'OUTOFSTOCK' | 'CARTACTIONFAILED'; // Rename to used method

export interface IAddOrderTemplateToCartComponent extends IComponent<IAddOrderTemplateToCartComponentProps> {
    onClick(): (event: React.MouseEvent<HTMLElement>, props: IAddOrderTemplateToCartComponentProps) => void;
}

const onClick = async (
    _event: React.MouseEvent<HTMLElement>,
    props: IAddOrderTemplateToCartComponentProps,
    setUploadStatus: (uploadStatus: addOrderTemplateToCartStatus) => void,
    openModal: (opened: boolean) => void,
    setLinesAddedToCartDialogOpen: (opened: boolean) => void,
    setConfirmAddedToCartDialogOpen: (opened: boolean) => void,
    setAlterItemAddedToCartDialogOpen: (opened: boolean) => void,
    setAddedLinesCount: (value: number) => void,
    isSupresed: boolean,
    isBackorder: boolean): Promise<void> => {
    // const gcBackOrderAllowed = props.customerInformation && props.customerInformation.ExtensionProperties ? props.customerInformation.ExtensionProperties?.find(f=>f.Key==="GCALLOWBACKORDERS")?.Value?.StringValue : '';
    // if(isBackorder && gcBackOrderAllowed !== '2')
    // {
    //     setConfirmAddedToCartDialogOpen(true);
    //     return;
    // }
    // else
    // {
        openModal(true);
        setUploadStatus('LOADING');
        const { context } = props;
        const addOrderTemplateToCartInput = new AddOrderTemplateToCartInput(props.data.orderTemplate.productList.Id,props.data.orderTemplate.orderTemplateLines);
        const addedCartLines = await addOrderTemplateToCartAction(addOrderTemplateToCartInput, context.actionContext);
        if(isSupresed)
        {
            setAlterItemAddedToCartDialogOpen(true);
        }
        setAddedLinesCount(addedCartLines.length);
        openModal(false);
        if(addedCartLines.length !== 0){
            setTimeout(() => {
                setLinesAddedToCartDialogOpen(true);
            }, 500);
        }

    // }
};

const onClickCustom = async (
    props: IAddOrderTemplateToCartComponentProps,
    setUploadStatus: (uploadStatus: addOrderTemplateToCartStatus) => void,
    openModal: (opened: boolean) => void,
    setLinesAddedToCartDialogOpen: (opened: boolean) => void,
    setConfirmAddedToCartDialogOpen: (opened: boolean) => void,
    setAlterItemAddedToCartDialogOpen: (opened: boolean) => void,
    setAddedLinesCount: (value: number) => void,
    isSupresed: boolean,
    isBackorder: boolean): Promise<void> => {
    openModal(true);
    setUploadStatus('LOADING');
    // setConfirmAddedToCartDialogOpen(true);
    const { context } = props;
    const addOrderTemplateToCartInput = new AddOrderTemplateToCartInput(props.data.orderTemplate.productList.Id,props.data.orderTemplate.orderTemplateLines);
    const addedCartLines = await addOrderTemplateToCartAction(addOrderTemplateToCartInput, context.actionContext);
    if(isSupresed)
    {
        setAlterItemAddedToCartDialogOpen(true);
    }
    setAddedLinesCount(addedCartLines.length);
    openModal(false);
    setTimeout(() => {
        setLinesAddedToCartDialogOpen(true);
    }, 500);
};


const onNoClickCustom = async (
    props: IAddOrderTemplateToCartComponentProps,
    setUploadStatus: (uploadStatus: addOrderTemplateToCartStatus) => void,
    openModal: (opened: boolean) => void,
    setLinesAddedToCartDialogOpen: (opened: boolean) => void,
    setConfirmAddedToCartDialogOpen: (opened: boolean) => void,
    setAlterItemAddedToCartDialogOpen: (opened: boolean) => void,
    setAddedLinesCount: (value: number) => void,
    isSupresed: boolean,
    isBackorder: boolean): Promise<void> => {
    openModal(true);
    setUploadStatus('LOADING');
    const { context } = props;
    const skipProdIds = props.data.orderTemplate.orderTemplateLines.filter(function(item){
        if(_inventoryLabel(item.fullProduct!, item.productAvailability!) === 'Out of stock' && (!item.fullProduct?.AlternateProduct?.serviceResp || !item.fullProduct?.AlternateProduct.currentProd ||(item.fullProduct?.AlternateProduct.serviceResp && item.fullProduct?.AlternateProduct.serviceResp[0].UseAltItemId !== 'Always')))
        {
            return item;
        }
        else
        {
            return false;
        }
    })?.map(x=>x.fullProduct?.ProductDetails.ItemId!);
    const addOrderTemplateToCartInput = new AddOrderTemplateToCartInput(props.data.orderTemplate.productList.Id,props.data.orderTemplate.orderTemplateLines,skipProdIds);
    const addedCartLines = await addOrderTemplateToCartAction(addOrderTemplateToCartInput, context.actionContext);
    if(isSupresed)
    {
        setAlterItemAddedToCartDialogOpen(true);
    }
    setAddedLinesCount(addedCartLines.length);
    openModal(false);
    if(addedCartLines.length !== 0){
    setTimeout(() => {
        setLinesAddedToCartDialogOpen(true);
    }, 500);
}
};


const AddToCartComponentActions = {
    onClick
};

const _inventoryLabel = (product: IFullProduct,productAvailability:IProductInventoryInformation[]): string | undefined => {
    if (productAvailability) {
        const productInventoryInformation = _getProductInventory(product, productAvailability);
        if (productInventoryInformation) {
            return productInventoryInformation.StockLevelLabel;
        }
    }

    return undefined;
};

const _getProductInventory = (product: IFullProduct, productInventoryCollection: IProductInventoryInformation[] | undefined): IProductInventoryInformation | undefined => {
    if (productInventoryCollection && productInventoryCollection.length > 0) {
        for (const productResult of productInventoryCollection) {
            if (product.ProductDetails.RecordId === productResult.ProductAvailableQuantity.ProductId) {
                return productResult;
            }
        }
    }
    return undefined;
};

export type addOrderTemplateToCartStatus = 'LOADING' | 'SUCCESS' | 'FAILED' | 'WAITING';

const _getModalText = (status: addOrderTemplateToCartStatus, props: IAddOrderTemplateToCartComponentProps) => {
    const { dialogStrings } = props;
    const { addToCartFailureMessage, addToCartSuccessMessage, addToCartProcessMessage } = dialogStrings;

    if (status === 'LOADING') {
        return `${addToCartProcessMessage}...`;
    }

    if (status === 'SUCCESS') {
        return addToCartSuccessMessage;
    }

    if (status === 'FAILED') {
        return addToCartFailureMessage;
    }

    return '';
};

let buttonEventReference: HTMLButtonElement | undefined;


const AddOrderTemplate: React.FC<IAddOrderTemplateToCartComponentProps> = (props: IAddOrderTemplateToCartComponentProps) => {
    const [uploadStatus, setUploadStatus] = useState<addOrderTemplateToCartStatus>('WAITING');
    const [modalOpen, setModalOpen] = useState(false);
    const [addedLinesCount, setAddedLinesCount] = useState(0);
    const [linesAddedToCartDialogOpen, setLinesAddedToCartDialogOpen] = useState(false);
    const [isConfirmAddedToCartDialogOpen, setConfirmAddedToCartDialogOpen] = useState(false);
    const [isAlterItemAddedToCartDialogOpen, setAlterItemAddedToCartDialogOpen] = useState(false);
    const isSupresed = props.data.orderTemplate.orderTemplateLines.find(line => line.fullProduct?.AlternateProduct?.serviceResp && line.fullProduct?.AlternateProduct?.serviceResp[0].UseAltItemId === 'Always' && line.fullProduct?.AlternateProduct?.currentProd)? true:false;
    // const isBackorder = props.data.orderTemplate.orderTemplateLines.find( line => _inventoryLabel(line.fullProduct!, line.productAvailability!) === 'Out of stock' && (!line.fullProduct?.AlternateProduct?.serviceResp || !line.fullProduct?.AlternateProduct.currentProd ||(line.fullProduct?.AlternateProduct.serviceResp && line.fullProduct?.AlternateProduct.serviceResp[0].UseAltItemId !== 'Always')))? true: false;

    const onClickHandler = async (event: React.MouseEvent<HTMLElement>) => {
        buttonEventReference = event.target as HTMLButtonElement;
        await AddToCartComponentActions.onClick(event, props, setUploadStatus, setModalOpen, setLinesAddedToCartDialogOpen,setConfirmAddedToCartDialogOpen, setAlterItemAddedToCartDialogOpen, setAddedLinesCount,isSupresed,false);
    };
    const closeLinesAddedToCartDialog = () => {
        setLinesAddedToCartDialogOpen(false);
        buttonEventReference?.focus();
    };

    const closeConfirmAddedToCartDialog =() => {
        setConfirmAddedToCartDialogOpen(false);
        // setDisabled(false);
    };

    const closeAlterItemAddedToCartDialog = () => {
        setAlterItemAddedToCartDialogOpen(false);
        // setDisabled(false);
    };

    const cofimationyesEvent =  () => {
        setConfirmAddedToCartDialogOpen(false);
     void onClickCustom(props, setUploadStatus, setModalOpen, setLinesAddedToCartDialogOpen,setConfirmAddedToCartDialogOpen, setAlterItemAddedToCartDialogOpen, setAddedLinesCount,isSupresed,false);
    };

    const noConfirmAddedToCartDialog =() => {
        setConfirmAddedToCartDialogOpen(false);
        void onNoClickCustom(props, setUploadStatus, setModalOpen, setLinesAddedToCartDialogOpen,setConfirmAddedToCartDialogOpen, setAlterItemAddedToCartDialogOpen, setAddedLinesCount,isSupresed,false);
    };

    const modalText = _getModalText(uploadStatus, props);
    const processStatusClass = uploadStatus.toLowerCase();
    const dialogStrings = { closeWindowButtonText: props.dialogStrings.closeWindowButtonText };
    const payLoad = getPayloadObject('click', props.telemetryContent!, TelemetryConstant.AddOrderTemplateToCart);
    const addOrderTemplateAttribute = getTelemetryAttributes(props.telemetryContent!, payLoad);

    const popupProps: IPopupProductsProps = {
        dialogStrings,
        className: 'msc-add-order-template-to-cart',
        processStatusClass,
        text: modalText,
        modalOpen,
        setModalOpen
    };

    const renderModalPopup = <PopupProducts {...popupProps} />;

    let products = props.data.orderTemplate.orderTemplateLines.map(line => line.fullProduct).filter(fullProduct => fullProduct) as FullProduct[];


    const supresedItems = props.data.orderTemplate.orderTemplateLines.filter(line => line.fullProduct?.AlternateProduct?.serviceResp && line.fullProduct?.AlternateProduct?.serviceResp[0].UseAltItemId === 'Always' && line.fullProduct?.AlternateProduct?.currentProd);
    let supreseditemList:string = '';
    if(supresedItems)
    {
        supreseditemList = supresedItems.map(line =>  `${line.fullProduct?.ProductDetails.ItemId  } - ${  line.fullProduct?.AlternateProduct?.currentProd?.productDtl?.ItemId}`).join(',');
    }
    const prodListSupresed =  props.data.orderTemplate.productList.ProductListLines?.map((item) =>{
        const prodLine = props.data.orderTemplate.orderTemplateLines.filter(x => x.fullProduct?.ProductDetails.RecordId === item.ProductId)[0];
        if(prodLine)
        {
            (prodLine.fullProduct?.AlternateProduct?.serviceResp && prodLine.fullProduct?.AlternateProduct?.serviceResp[0].UseAltItemId === 'Always' && prodLine.fullProduct?.AlternateProduct?.currentProd);
            {
                item.ProductId = prodLine.fullProduct?.AlternateProduct?.currentProd?.productDtl?.RecordId;
            }
        }
        return item;
    });

    const prodList = props.data.orderTemplate.productList;
    prodList.ProductListLines = prodListSupresed;
    products = products.filter(function(item,idx){
        var isActive = false;

        if(item && item.ProductDetails && item.ProductDetails.ExtensionProperties)
        {
            const isActiveProps = item.ProductDetails.ExtensionProperties.filter(x=> x.Key === 'IsActive');
            if(!isActive && isActiveProps && isActiveProps[0] && isActiveProps[0].Value !== undefined)
            {
                isActive= isActiveProps[0].Value.BooleanValue !== undefined ? isActiveProps[0].Value.BooleanValue : false;
            }
        }
        if(isActive)
        {return item;}
        else
        {return false;}
    });

    return (
        <>
            {renderModalPopup}
            <button
                className={classnames('msc-add-order-template-to-cart msc-btn', props.className, props.data.orderTemplate.totalLines === 0 ? 'disabled' : '')}
                aria-label={props.addToCartText}
                disabled={props.data.orderTemplate.totalLines === 0}
                type='button'
                onClick={onClickHandler}
                {...addOrderTemplateAttribute}
            >
                {props.addToCartText}
            </button>
            <ConfirmAddedToCartDialog
            className='msc-confirm-added-to-cart'
            modalHeader={props.dialogStrings.confirmHeaderText}
            modalBodyText={props.dialogStrings.confirmBodyText}
            modalOpen={isConfirmAddedToCartDialogOpen}
            onClose={closeConfirmAddedToCartDialog}
            alternateItem={false}
            _addItemToCartCustom={cofimationyesEvent}
            quantity={1}
            onNoClick={noConfirmAddedToCartDialog}
            // onClosePopup={closeConfirmAddedToCartWithQuickViewDialog}
            />
            <ConfirmAddedToCartDialog
            className='msc-confirm-added-to-cart'
            modalHeader={props.dialogStrings.alternateItemHeaderText}
            modalBodyText={format(props.dialogStrings.alternateItemBodyText??'',supreseditemList)}
            modalOpen={isAlterItemAddedToCartDialogOpen}
            onClose={closeAlterItemAddedToCartDialog}
            alternateItem={true}
            _addItemToCartCustom={cofimationyesEvent}
            quantity={1}
            onNoClick={closeAlterItemAddedToCartDialog}
            // onClosePopup={closeConfirmAddedToCartWithQuickViewDialog}
            />
            <LinesAddedToCartDialog
                className='msc-lines-added-to-cart-dialog'
                id={props.id}
                typeName={props.typeName}
                context={props.context}
                dialogStrings={props.dialogStrings.linesAddedToCartDialogResources}
                data={{
                    linesCount: addedLinesCount,
                    orderTemplate: isSupresed ? prodList : props.data.orderTemplate.productList,
                    products
                }}
                modalOpen={linesAddedToCartDialogOpen}
                onClose={closeLinesAddedToCartDialog}
            />
        </>
    );

};

export const AddOrderTemplateToCartComponent: React.FunctionComponent<IAddOrderTemplateToCartComponentProps> =

// @ts-expect-error -- As per design, custom component
 msdyn365Commerce.createComponent<IAddOrderTemplateToCartComponent>(
     'AddOrderTemplate',
     { component: AddOrderTemplate, ...AddToCartComponentActions }
 );
